import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';

interface SharedCompanyCarToggleProps {
  vehicleId: number;
}

export function SharedCompanyCarToggle({ vehicleId }: SharedCompanyCarToggleProps) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/api/shared-car/vehicles/${vehicleId}`)
      .then((response) => {
        if (cancelled) return;
        setEnabled(Boolean(response.data?.data?.isSharedCompanyCar));
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [vehicleId]);

  const onChange = async (next: boolean) => {
    setSaving(true);
    setEnabled(next);
    try {
      await api.put(`/api/shared-car/vehicles/${vehicleId}`, { isSharedCompanyCar: next });
    } catch {
      setEnabled(!next);
      toast.error(t('fleet.sharedCompanyCar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          disabled={loading || saving}
          onChange={(event) => { void onChange(event.target.checked); }}
          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
        />
        <span className="text-sm font-medium text-textDefault">{t('fleet.sharedCompanyCar')}</span>
      </label>
      <p className="mt-1 text-xs text-textTertiary">{t('fleet.sharedCompanyCarHint')}</p>
    </div>
  );
}
